import {
  AppNumberInput,
  AppSimpleSelect,
  FieldRow,
} from 'components/FormElements';
import Spacing from 'components/Spacing';
import { carrySettings } from 'further-ui/validations';
import { Control, Controller } from 'react-hook-form';
import { z } from 'zod';
import useCarriedInterestFeeTemplatesOptions from './hooks/useCarriedInterestFeeTemplatesOptions';
import { PreferredReturnAccrualMethod } from 'further-types/lp/carry-settings';
import AppCheckbox from 'components/FormElements/AppCheckBox';
import { FormLabel, styled, TextField } from '@mui/material';
import { Row } from 'components/Layout';
import CarriedInterestRate from './CarriedInterestRate';
import useCarriedInterestFeeTemplate from './hooks/useCarriedInterestFeeTemplate';
import DraggableList from 'components/DraggableList';

const StyledDraggableList = styled(DraggableList)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(3),
}));

type FormValues = z.infer<
  typeof carrySettings.CarriedInterestFeeTemplatesSettings
>;

type Props = {
  index: number;
  control: Control<FormValues>;
};

const CarriedInterestFeeTemplate: React.FC<Props> = ({ index, control }) => {
  const {
    handleChangePreferredReturnAccrualMethod,
    hasOtherDefaultTemplate,
    useCompoundAccrualMethod,
    useSimpleAccrualMethod,
    appendCarriedInterestRate,
    removeCarriedInterestRate,
    carriedInterestRateFields,
    handleCarriedInterestRatesReorder,
  } = useCarriedInterestFeeTemplate(index);

  const {
    defaultCarriedInterestRate,
    preferredReturnAccrualMethodOptions,
    preferredReturnAccrualPeriodOptions,
    preferredReturnCompoundingPeriodOptions,
    preferredReturnCalculationEndOptions,
    preferredReturnBasisOptions,
  } = useCarriedInterestFeeTemplatesOptions({ useSimpleAccrualMethod });

  const handleAddCarriedInterestRate = () => {
    appendCarriedInterestRate(defaultCarriedInterestRate);
  };

  const handleRemoveCarriedInterestRate = (index: number) => {
    if (carriedInterestRateFields.length === 1) return;

    removeCarriedInterestRate(index);
  };

  return (
    <Spacing>
      <FieldRow title="Template name">
        <Controller
          name={`carriedInterestFeeTemplates.${index}.name`}
          control={control}
          render={({ fieldState: { error }, field }) => (
            <TextField
              {...field}
              fullWidth
              error={!!error}
              helperText={error?.message}
              size="small"
            />
          )}
        />
      </FieldRow>
      <FieldRow title="Carried interest rates">
        <StyledDraggableList
          onReorder={({ orderedItemIds }) =>
            handleCarriedInterestRatesReorder(orderedItemIds)
          }
        >
          {carriedInterestRateFields.map((field, rateIndex) => (
            <DraggableList.Item key={field.id} id={field.id}>
              <CarriedInterestRate
                index={rateIndex}
                templateIndex={index}
                control={control}
                onAddRate={handleAddCarriedInterestRate}
                onRemoveRate={handleRemoveCarriedInterestRate}
                showThreshold={rateIndex > 0}
                allowDelete={carriedInterestRateFields.length > 1}
              />
            </DraggableList.Item>
          ))}
        </StyledDraggableList>
      </FieldRow>
      {carriedInterestRateFields.length > 1 && (
        <FieldRow title=" ">
          <Controller
            name={`carriedInterestFeeTemplates.${index}.carriedInterestPartnerCatchupBetweenRateThresholds`}
            control={control}
            render={({ fieldState: { error }, field: { onChange, value } }) => (
              <FormLabel>
                <Row centered>
                  <AppCheckbox
                    checked={!!value}
                    onChange={(e) => onChange(e.target.checked)}
                    error={!!error}
                    helperText={error?.message}
                  />
                  <span>
                    Carried interest partner catch-up between rate threshold(s)
                  </span>
                </Row>
              </FormLabel>
            )}
          />
        </FieldRow>
      )}
      <FieldRow title="Preferred return rate" centerTitle>
        <Controller
          name={`carriedInterestFeeTemplates.${index}.preferredReturnRate`}
          control={control}
          render={({ fieldState: { error }, field: { onChange, ...rest } }) => (
            <AppNumberInput
              {...rest}
              decimals={2}
              onChange={(value: number) => {
                onChange(value);
              }}
              error={!!error}
              helperText={error?.message}
              percentEndAdornment
            />
          )}
        />
      </FieldRow>
      <FieldRow title="Preferred return accrual method" centerTitle>
        <Controller
          name={`carriedInterestFeeTemplates.${index}.preferredReturnAccrualMethod`}
          control={control}
          render={({ fieldState: { error }, field: { value } }) => (
            <AppSimpleSelect
              options={preferredReturnAccrualMethodOptions}
              fullWidth
              error={!!error}
              errorMessage={error?.message}
              value={value}
              onChange={(e) =>
                handleChangePreferredReturnAccrualMethod(
                  e.target.value as PreferredReturnAccrualMethod,
                )
              }
            />
          )}
        />
      </FieldRow>
      <FieldRow
        title="Preferred return accrual period"
        centerTitle
        tooltipText="This is the period after which preferred return is due. For example, a monthly accrual period means that each month the interest due increases, but in the middle of the month the interest due is the figure from the end of the past month."
      >
        <Controller
          name={`carriedInterestFeeTemplates.${index}.preferredReturnAccrualPeriod`}
          control={control}
          render={({ fieldState: { error }, field: { onChange, value } }) => (
            <AppSimpleSelect
              options={preferredReturnAccrualPeriodOptions}
              fullWidth
              error={!!error}
              errorMessage={error?.message}
              value={value}
              onChange={(e) => onChange(e.target.value)}
            />
          )}
        />
      </FieldRow>
      {useCompoundAccrualMethod && (
        <FieldRow
          title="Preferred return compounding period"
          centerTitle
          tooltipText="This is the period after which preferred return will compound and be considered within the basis of future preferred return calculations. For example, a monthly compounding period means that at the end of each month preferred return interest is added to the principal for future interest calculations, but in the middle of the month the system will take the principal and interest sum from the end of the past month."
        >
          <Controller
            name={`carriedInterestFeeTemplates.${index}.preferredReturnCompoundingPeriod`}
            control={control}
            render={({ fieldState: { error }, field: { onChange, value } }) => (
              <AppSimpleSelect
                options={preferredReturnCompoundingPeriodOptions}
                fullWidth
                error={!!error}
                errorMessage={error?.message}
                value={value}
                onChange={(e) => onChange(e.target.value)}
              />
            )}
          />
        </FieldRow>
      )}
      <FieldRow title="Preferred return calculation end" centerTitle>
        <Controller
          name={`carriedInterestFeeTemplates.${index}.preferredReturnCalculationEnd`}
          control={control}
          render={({ fieldState: { error }, field: { onChange, value } }) => (
            <AppSimpleSelect
              options={preferredReturnCalculationEndOptions}
              fullWidth
              error={!!error}
              errorMessage={error?.message}
              value={value}
              onChange={(e) => onChange(e.target.value)}
            />
          )}
        />
      </FieldRow>
      <FieldRow title="Preferred return basis" centerTitle>
        <Controller
          name={`carriedInterestFeeTemplates.${index}.preferredReturnBasis`}
          control={control}
          render={({ fieldState: { error }, field: { onChange, value } }) => (
            <AppSimpleSelect
              options={preferredReturnBasisOptions}
              fullWidth
              error={!!error}
              errorMessage={error?.message}
              value={value}
              onChange={(e) => onChange(e.target.value)}
            />
          )}
        />
      </FieldRow>
      <FieldRow
        title="Partner catchup speed"
        centerTitle
        tooltipText={
          <>
            This percentage determines how profits are allocated to the carried
            interest partner (CP) after the limited partners (LPs) receive their
            preferred return. For example:
            <br />- <b>100%:</b> The CP receives 100% of profits after the LPs'
            preferred return, until they reach their full carried interest
            entitlement, ensuring a rapid catch-up.
            <br /> - <b>50%:</b> The CP receives 50% of profits during the
            catch-up phase, with the other 50% going to general final profit
            split. This extends the time it takes the CP to reach its full
            carried interest entitlement. It does not typically reduce the total
            amount the CP receives if the fund performs well.
            <br />- Adjust this percentage to modify how quickly the CP catches
            up to its carried interest share before the final profit split
            occurs.
          </>
        }
      >
        <Controller
          name={`carriedInterestFeeTemplates.${index}.partnerCatchupSpeed`}
          control={control}
          render={({ fieldState: { error }, field: { onChange, ...rest } }) => (
            <AppNumberInput
              {...rest}
              decimals={2}
              onChange={(value: number) => {
                onChange(value);
              }}
              error={!!error}
              helperText={error?.message}
              percentEndAdornment
            />
          )}
        />
      </FieldRow>
      <FieldRow title="Default carried interest setting" centerTitle>
        <Controller
          name={`carriedInterestFeeTemplates.${index}.isDefault`}
          control={control}
          render={({ fieldState: { error }, field: { onChange, value } }) => (
            <AppCheckbox
              checked={!!value}
              onChange={(e) => onChange(e.target.checked)}
              error={!!error}
              helperText={error?.message}
              disabled={hasOtherDefaultTemplate}
            />
          )}
        />
      </FieldRow>
    </Spacing>
  );
};

export default CarriedInterestFeeTemplate;
